/**
 * RTK Query slice for the out-of-area onboarding request flow
 * (GG-169) — the become-a-vendor form's "request anyway" path.
 * Contract in @gearghost/api-schemas/serviceArea.
 */
import { createApi } from "@reduxjs/toolkit/query/react";
import type { z } from "zod";
import {
  SERVICE_AREA_ROUTES,
  MyServiceAreaRequestResultSchema,
  MyServiceAreaRequestEnvelopeSchema,
  unwrapMyServiceAreaRequest,
  CreateServiceAreaRequestResultSchema,
  CreateServiceAreaRequestResultEnvelopeSchema,
  unwrapCreateServiceAreaRequestResult,
  ServiceAreaCheckResultSchema,
  ServiceAreaCheckEnvelopeSchema,
  unwrapServiceAreaCheck,
  type CreateServiceAreaRequest,
} from "@gearghost/api-schemas/serviceArea";
import axiosBaseQuery from "./axiosBaseQuery";

type MyRequestResult = z.infer<typeof MyServiceAreaRequestResultSchema>;
type CreateRequestResult = z.infer<typeof CreateServiceAreaRequestResultSchema>;
type CheckResult = z.infer<typeof ServiceAreaCheckResultSchema>;

/** Client-side arg for the check: plain numbers (the schema's z.coerce
 * input type is `unknown`, which would make a useless arg type). */
export interface ServiceAreaCheckArgs {
  lat: number;
  lng: number;
}

export const serviceAreaApi = createApi({
  reducerPath: "serviceAreaApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["ServiceAreaRequest"],
  endpoints: (builder) => ({
    getMyServiceAreaRequest: builder.query<MyRequestResult, void>({
      query: () => ({ url: SERVICE_AREA_ROUTES.request }),
      rawResponseSchema: MyServiceAreaRequestEnvelopeSchema,
      transformResponse: unwrapMyServiceAreaRequest,
      responseSchema: MyServiceAreaRequestResultSchema,
      providesTags: ["ServiceAreaRequest"],
    }),
    createServiceAreaRequest: builder.mutation<CreateRequestResult, CreateServiceAreaRequest>({
      query: (body) => ({
        url: SERVICE_AREA_ROUTES.request,
        method: "POST",
        body,
      }),
      rawResponseSchema: CreateServiceAreaRequestResultEnvelopeSchema,
      transformResponse: unwrapCreateServiceAreaRequestResult,
      responseSchema: CreateServiceAreaRequestResultSchema,
      invalidatesTags: ["ServiceAreaRequest"],
    }),
    // The become-a-vendor Location step's pre-submit read (GG-504): the
    // same gate the become-vendor POST runs, asked before the applicant
    // fills the remaining steps. Lazy — fired on Continue, not on render.
    checkServiceArea: builder.query<CheckResult, ServiceAreaCheckArgs>({
      query: ({ lat, lng }) => ({
        url: SERVICE_AREA_ROUTES.check,
        params: { lat, lng },
      }),
      rawResponseSchema: ServiceAreaCheckEnvelopeSchema,
      transformResponse: unwrapServiceAreaCheck,
      responseSchema: ServiceAreaCheckResultSchema,
    }),
  }),
});

export const {
  useGetMyServiceAreaRequestQuery,
  useCreateServiceAreaRequestMutation,
  useLazyCheckServiceAreaQuery,
} = serviceAreaApi;
